import { useEffect, useRef, useState } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { firebaseAuth, firebaseApp } from "../lib/firebase";

/**
 * Diagnostic page for Firebase Phone Auth `auth/network-request-failed`.
 * - Prints loaded Firebase config (API key partially redacted)
 * - Prints current origin (must match Firebase Authorized Domains)
 * - Tries to load reCAPTCHA + send OTP to a test number, and reports the EXACT failing call.
 * - Pings identitytoolkit.googleapis.com with the API key to detect referrer-restriction blocks.
 */
export default function FirebaseDebug() {
  const [phone, setPhone] = useState("");
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const recaptchaRef = useRef(null);

  const log = (msg, kind = "info") =>
    setLogs((prev) => [...prev, { ts: new Date().toLocaleTimeString(), msg, kind }]);

  const cfg = firebaseApp?.options || {};
  const redact = (v) => (v ? `${v.slice(0, 6)}…${v.slice(-4)}` : "(missing)");

  // 1) Probe identitytoolkit directly with the API key. This catches:
  //    - API key not enabled for Identity Toolkit API
  //    - HTTP referrer restrictions blocking this origin in GCP
  //    - A Service Worker on a sibling origin (e.g. apex vs www) that hijacks fetches
  const probeIdentityToolkit = async () => {
    log("Probing identitytoolkit.googleapis.com with current API key…");
    let r;
    try {
      r = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${cfg.apiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken: "diagnostic-invalid-token" }),
        },
      );
    } catch (e) {
      log(
        `✗ Network failure reaching identitytoolkit.googleapis.com: ${e.message}. ` +
          `Likely a Service Worker (possibly on a sibling origin like apex-vs-www) is blocking. ` +
          `Try opening the apex/www twin URL — whichever you didn't try — and unregister the SW there.`,
        "err",
      );
      return false;
    }
    let text = "";
    let data = {};
    try {
      text = await r.text();
    } catch (e) {
      log(
        `✗ Couldn't read response body (${e.message}). This almost always means a Service ` +
          `Worker intercepted and returned a pre-consumed Response. The SW is registered on ` +
          `a sibling origin (apex vs www) — open ${window.location.origin.replace("://www.", "://").replace("://", "://www.")}/debug/firebase ` +
          `and unregister the SW there too.`,
        "err",
      );
      return false;
    }
    try { data = JSON.parse(text); } catch (e) { console.debug("[FirebaseDebug] non-json body:", e?.message); }
    if (r.status === 400 && data?.error?.message === "INVALID_ID_TOKEN") {
      log("✓ Identity Toolkit reachable. API key + referrer are OK.", "ok");
      return true;
    }
    if (r.status === 403) {
      log(
        `✗ 403 from Identity Toolkit: ${data?.error?.message || "unknown"}. ` +
          `Likely cause: API key has HTTP referrer restrictions in Google Cloud Console ` +
          `that don't include this domain (${window.location.origin}), OR Identity Toolkit API is disabled.`,
        "err",
      );
      return false;
    }
    log(`Unexpected status ${r.status} ${r.statusText}. Raw body (first 400 chars): ${text.slice(0, 400) || "(empty)"}`, "warn");
    if (data?.error?.message) {
      log(`Firebase error code: ${data.error.message}`, "warn");
    } else if (!text) {
      log(
        "Empty body usually means: (a) Service Worker on a sibling origin intercepted and stripped it, " +
          "(b) browser extension/AdBlocker is blocking, or " +
          "(c) Cloudflare/edge is blocking the API key.",
        "warn",
      );
    }
    return false;
  };

  // 2) Try the actual Phone Auth flow with the user's number.
  const tryPhoneAuth = async () => {
    if (!/^\d{10}$/.test(phone)) {
      log("Enter a 10-digit Indian mobile to attempt OTP send.", "warn");
      return;
    }
    log("Building invisible reCAPTCHA…");
    let verifier;
    try {
      verifier = new RecaptchaVerifier(firebaseAuth, recaptchaRef.current, { size: "invisible" });
      await verifier.render();
      log("✓ reCAPTCHA loaded successfully.", "ok");
    } catch (e) {
      log(`✗ reCAPTCHA build failed: ${e.code || ""} ${e.message}`, "err");
      return;
    }
    log(`Sending OTP to +91${phone}…`);
    try {
      await signInWithPhoneNumber(firebaseAuth, `+91${phone}`, verifier);
      log("✓ OTP request accepted. Check your SMS.", "ok");
    } catch (e) {
      const code = e?.code || "";
      log(`✗ signInWithPhoneNumber failed: ${code} — ${e.message}`, "err");
      if (code === "auth/network-request-failed") {
        log(
          "Hint: most common cause now is API-key HTTP-referrer restriction in Google Cloud Console. " +
            `Allow ${window.location.origin}/* in: GCP Console → APIs & Services → Credentials → ` +
            "your Browser key → Application restrictions → HTTP referrers.",
          "warn",
        );
      }
    }
  };

  const run = async () => {
    setRunning(true);
    setLogs([]);
    log(`Origin: ${window.location.origin}`);
    log(`Firebase projectId: ${cfg.projectId || "(missing)"}`);
    log(`Firebase authDomain: ${cfg.authDomain || "(missing)"}`);
    log(`Firebase apiKey: ${redact(cfg.apiKey)}`);
    log(`Firebase appId: ${redact(cfg.appId)}`);
    log(`User-Agent: ${navigator.userAgent}`);
    log(`Service Worker controlled: ${navigator.serviceWorker?.controller ? "yes" : "no"}`);

    if (!cfg.apiKey || !cfg.projectId || !cfg.authDomain || !cfg.appId) {
      log(
        "✗ One or more REACT_APP_FIREBASE_* env vars are missing in this build. " +
          "Add them to your deployment environment and redeploy the frontend.",
        "err",
      );
      setRunning(false);
      return;
    }

    const ok = await probeIdentityToolkit();
    if (ok) await tryPhoneAuth();
    setRunning(false);
  };

  // Auto-run probe (without OTP) on page load
  useEffect(() => {
    run().catch(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground p-6 font-mono text-sm">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="font-serif text-3xl mb-2">Firebase Phone Auth Diagnostics</h1>
          <p className="text-muted-foreground text-xs">
            This page probes your Firebase setup and tries to reproduce
            <code className="mx-1 px-1 bg-muted">auth/network-request-failed</code>
            so we can pinpoint the failing layer.
          </p>
        </div>

        <div className="card-flat p-4">
          <label className="block mb-2 text-xs uppercase tracking-wider text-muted-foreground">
            Test number (optional, +91)
          </label>
          <div className="flex gap-2">
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
              placeholder="9876543210"
              className="input-dark flex-1 px-3 py-2"
              data-testid="fb-debug-phone"
            />
            <button
              onClick={run}
              disabled={running}
              className="btn-primary px-4 py-2 text-xs"
              data-testid="fb-debug-run"
            >
              {running ? "Running…" : "Run diagnostics"}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Leave the phone blank to only probe config + Identity Toolkit reachability (no SMS sent).
          </p>
          <button
            onClick={async () => {
              if (!navigator.serviceWorker) {
                log("This browser has no Service Worker support.", "warn");
                return;
              }
              const regs = await navigator.serviceWorker.getRegistrations();
              for (const r of regs) await r.unregister();
              const keys = await caches.keys();
              for (const k of keys) await caches.delete(k);
              log(`Unregistered ${regs.length} SW(s) and cleared ${keys.length} cache(s). Reload (Ctrl+Shift+R) and retry.`, "ok");
            }}
            className="btn-ghost mt-3 px-3 py-1.5 text-xs"
            data-testid="fb-debug-unregister-sw"
          >
            Unregister Service Worker + clear caches
          </button>
        </div>

        <div className="card-flat p-4">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Output</h2>
          <pre className="whitespace-pre-wrap text-xs leading-6">
            {logs.map((l, i) => (
              <div
                key={`${l.ts}-${i}`}
                className={
                  l.kind === "err"
                    ? "text-red-400"
                    : l.kind === "warn"
                    ? "text-yellow-400"
                    : l.kind === "ok"
                    ? "text-green-400"
                    : "text-foreground"
                }
              >
                [{l.ts}] {l.msg}
              </div>
            ))}
          </pre>
        </div>

        <div ref={recaptchaRef} id="estima-debug-recaptcha" />

        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            <strong>Tip:</strong> Open DevTools → Network and filter for{" "}
            <code>identitytoolkit</code> or <code>recaptcha</code> while running this page. Share a screenshot
            with the agent for fastest diagnosis.
          </p>
        </div>
      </div>
    </div>
  );
}
